import React, { useState, useEffect } from 'react'

interface SecurityGateProps {
  onAuthenticated: () => void
}

export const SecurityGate: React.FC<SecurityGateProps> = ({ onAuthenticated }) => {
  const [currentStep, setCurrentStep] = useState<'temp' | 'setup' | 'normal'>('temp')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLocked, setIsLocked] = useState(false)
  const [attempts, setAttempts] = useState(0)
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  // System credentials
  const TEMP_PASSWORD = "STRATOS-SETUP-2025"  // One-time setup password
  const MAX_ATTEMPTS = 3
  const LOCKOUT_TIME = 300000 // 5 minutes in milliseconds

  useEffect(() => {
    // Check if already authenticated
    const isAuthenticated = sessionStorage.getItem('stratos_authenticated')
    if (isAuthenticated === 'true') {
      onAuthenticated()
      return
    }

    // Check if master password is already set
    const hasMasterPassword = localStorage.getItem('stratos_master_password')
    if (hasMasterPassword) {
      setCurrentStep('normal')
    } else {
      setCurrentStep('temp')
    }

    // Check for lockout status
    const lockoutEnd = localStorage.getItem('stratos_lockout')
    if (lockoutEnd && Date.now() < parseInt(lockoutEnd)) {
      setIsLocked(true)
      setTimeRemaining(Math.ceil((parseInt(lockoutEnd) - Date.now()) / 1000))
    }

    // Load attempt count
    const storedAttempts = localStorage.getItem('stratos_attempts')
    if (storedAttempts) {
      setAttempts(parseInt(storedAttempts))
    }
  }, [onAuthenticated])

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (isLocked && timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setIsLocked(false)
            setAttempts(0)
            localStorage.removeItem('stratos_lockout')
            localStorage.removeItem('stratos_attempts')
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [isLocked, timeRemaining])

  const handleTempPasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isLocked) return

    setIsLoading(true)

    setTimeout(() => {
      if (password === TEMP_PASSWORD) {
        // Temp password correct, move to setup
        setCurrentStep('setup')
        setPassword('')
        localStorage.removeItem('stratos_attempts')
      } else {
        // Failed attempt
        handleFailedAttempt()
      }
      setIsLoading(false)
    }, 1500)
  }

  const handlePasswordSetup = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (newPassword.length < 8) {
      alert('Password must be at least 8 characters long')
      return
    }

    if (newPassword !== confirmPassword) {
      alert('Passwords do not match')
      return
    }

    setIsLoading(true)

    setTimeout(() => {
      // Save the new master password
      localStorage.setItem('stratos_master_password', newPassword)
      localStorage.setItem('stratos_setup_complete', 'true')
      
      // Auto-authenticate after setup
      sessionStorage.setItem('stratos_authenticated', 'true')
      onAuthenticated()
    }, 1000)
  }

  const handleNormalLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (isLocked) return

    setIsLoading(true)

    setTimeout(() => {
      const savedPassword = localStorage.getItem('stratos_master_password')
      if (password === savedPassword) {
        // Success
        sessionStorage.setItem('stratos_authenticated', 'true')
        localStorage.removeItem('stratos_attempts')
        localStorage.removeItem('stratos_lockout')
        onAuthenticated()
      } else {
        // Failed attempt
        handleFailedAttempt()
      }
      setIsLoading(false)
    }, 1500)
  }

  const handleFailedAttempt = () => {
    const newAttempts = attempts + 1
    setAttempts(newAttempts)
    localStorage.setItem('stratos_attempts', newAttempts.toString())
    
    if (newAttempts >= MAX_ATTEMPTS) {
      // Lock account
      const lockoutEnd = Date.now() + LOCKOUT_TIME
      localStorage.setItem('stratos_lockout', lockoutEnd.toString())
      setIsLocked(true)
      setTimeRemaining(Math.ceil(LOCKOUT_TIME / 1000))
    }
    
    setPassword('')
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const renderTempPasswordStep = () => (
    <>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-yellow-500 rounded flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
        <div>
          <h2 className="text-yellow-400 font-semibold">Initial Setup Required</h2>
          <p className="text-gray-400 text-sm">Enter temporary password to configure system</p>
        </div>
      </div>

      <form onSubmit={handleTempPasswordSubmit}>
        <div className="mb-6">
          <label className="block text-gray-300 text-sm font-medium mb-3">
            Step 1: Enter temporary setup password
          </label>
          
          <div className="relative">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLocked || isLoading}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 disabled:opacity-50"
              placeholder="Enter temporary setup password"
              required
            />
          </div>
          
          <div className="mt-2 p-3 bg-yellow-900 border border-yellow-600 rounded-lg">
            <p className="text-yellow-300 text-sm">
              <strong>Temporary Password:</strong> <code className="bg-yellow-800 px-2 py-1 rounded">STRATOS-SETUP-2025</code>
            </p>
            <p className="text-yellow-400 text-xs mt-1">
              Use this one-time password to setup your personal master password
            </p>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLocked || isLoading || !password}
          className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verifying...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Continue Setup
            </>
          )}
        </button>
      </form>
    </>
  )

  const renderPasswordSetup = () => (
    <>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-green-500 rounded flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
          </svg>
        </div>
        <div>
          <h2 className="text-green-400 font-semibold">Create Master Password</h2>
          <p className="text-gray-400 text-sm">Set your permanent access credentials</p>
        </div>
      </div>

      <form onSubmit={handlePasswordSetup}>
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              New Master Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
              placeholder="Enter your master password"
              required
              minLength={8}
            />
          </div>
          
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
              placeholder="Confirm your master password"
              required
              minLength={8}
            />
          </div>
        </div>

        <div className="mb-6 p-3 bg-blue-900 border border-blue-600 rounded-lg">
          <p className="text-blue-300 text-sm">
            <strong>Password Requirements:</strong>
          </p>
          <ul className="text-blue-400 text-xs mt-1 space-y-1">
            <li>• Minimum 8 characters</li>
            <li>• This will be your permanent access password</li>
            <li>• Store it securely - no recovery option available</li>
          </ul>
        </div>

        <button
          type="submit"
          disabled={isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Setting up...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Create Master Password
            </>
          )}
        </button>
      </form>
    </>
  )

  const renderNormalLogin = () => (
    <>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-cyan-500 rounded flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
        </div>
        <div>
          <h2 className="text-cyan-400 font-semibold">Secure Access Required</h2>
          <p className="text-gray-400 text-sm">Enter your master password</p>
        </div>
      </div>

      <form onSubmit={handleNormalLogin}>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-cyan-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
              1
            </div>
            <div className="flex-1 h-px bg-gray-600"></div>
            <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center text-gray-400 text-sm font-bold">
              2
            </div>
          </div>
          
          <label className="block text-gray-300 text-sm font-medium mb-3">
            Step 1: Enter your master password
          </label>
          
          <div className="relative">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLocked || isLoading}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 disabled:opacity-50"
              placeholder="Enter your master password"
              required
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLocked || isLoading || !password}
          className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verifying...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Verify Password
            </>
          )}
        </button>
      </form>
    </>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Security Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <span className="text-white font-bold text-2xl">S</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">StratOS Platform</h1>
          <p className="text-gray-400 text-sm">Institutional Investment Intelligence</p>
        </div>

        {/* Security Gate */}
        <div className="bg-gray-800 border border-cyan-500 rounded-lg p-6 shadow-2xl">
          {/* Security Level Indicator */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Security Level</span>
              <span className="bg-red-600 text-white text-xs px-2 py-1 rounded font-semibold">MAXIMUM</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div className="bg-gradient-to-r from-red-500 to-red-600 h-2 rounded-full w-full"></div>
            </div>
          </div>

          {/* Lockout Warning */}
          {isLocked && (
            <div className="bg-red-900 border border-red-500 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-red-400 font-semibold">Account Temporarily Locked</span>
              </div>
              <p className="text-red-300 text-sm mb-2">Time remaining: {formatTime(timeRemaining)}</p>
            </div>
          )}

          {/* Dynamic Content Based on Step */}
          {currentStep === 'temp' && renderTempPasswordStep()}
          {currentStep === 'setup' && renderPasswordSetup()}
          {currentStep === 'normal' && renderNormalLogin()}

          {/* Attempt Counter - only show for normal login */}
          {currentStep !== 'setup' && (
            <div className="mt-4 text-center">
              <p className="text-gray-400 text-sm">
                {isLocked 
                  ? `Account locked after ${MAX_ATTEMPTS} failed attempts`
                  : `Attempts remaining: ${MAX_ATTEMPTS - attempts}/${MAX_ATTEMPTS}`
                }
              </p>
            </div>
          )}

          {/* Security Notice */}
          <div className="mt-6 pt-4 border-t border-gray-700">
            <p className="text-gray-500 text-xs text-center">
              Protected by advanced security measures including browser fingerprinting,
              session management, and automatic logout on page exit.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-gray-500 text-sm">StratOS v1.0.0 • Secure Access Portal</p>
        </div>
      </div>
    </div>
  )
}